import { Modal, Radio, Space, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../../components/Input/TextInput.tsx"
import type { EducationProjectTypeRef } from "./types/education.types.ts"

const OTHER_TYPE_NAME = "other"
const MIN_DESCRIPTION_LENGTH = 3
const MAX_DESCRIPTION_LENGTH = 300

const isOtherType = (name: string) =>
  name.trim().toLowerCase() === OTHER_TYPE_NAME

export interface ProjectTypeConfirmSubmission {
  project_type_id: string
  project_description?: string
}

interface ProjectTypeConfirmProps {
  open: boolean
  choices: EducationProjectTypeRef[]
  submitting?: boolean
  onCancel: () => void
  onConfirm: (body: ProjectTypeConfirmSubmission) => void
}

/**
 * Opened either proactively (the lead has more than one project type) or
 * reactively (a 422 `PROJECT_TYPE_REQUIRED` handed back `choices`) — same
 * component either way, since the choice itself is identical. "Other"
 * additionally requires a 3-300 character description; the field is a
 * single-line `TextInput` (not a `TextArea`) specifically so Enter submits,
 * per the workspace's Tab/Enter form rule.
 */
export function ProjectTypeConfirm({
  open,
  choices,
  submitting,
  onCancel,
  onConfirm
}: ProjectTypeConfirmProps) {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [description, setDescription] = useState("")

  useEffect(() => {
    if (open) {
      setSelectedId(null)
      setDescription("")
    }
  }, [open])

  const selected = choices.find((choice) => choice.id === selectedId) ?? null
  const needsDescription = !!selected && isOtherType(selected.name)
  const trimmedDescription = description.trim()
  const descriptionValid =
    trimmedDescription.length >= MIN_DESCRIPTION_LENGTH &&
    trimmedDescription.length <= MAX_DESCRIPTION_LENGTH

  const canSubmit = !!selected && (!needsDescription || descriptionValid)

  const handleSubmit = () => {
    if (!selected || !canSubmit) return
    onConfirm({
      project_type_id: selected.id,
      ...(needsDescription ? { project_description: trimmedDescription } : {})
    })
  }

  return (
    <Modal
      open={open}
      title={t("projectEducation.projectTypeConfirm.title")}
      okText={t("projectEducation.projectTypeConfirm.confirm")}
      cancelText={t("projectEducation.projectTypeConfirm.cancel")}
      onCancel={onCancel}
      onOk={handleSubmit}
      okButtonProps={{ disabled: !canSubmit, loading: submitting }}
      destroyOnHidden
    >
      <Typography.Paragraph type={"secondary"}>
        {t("projectEducation.projectTypeConfirm.description")}
      </Typography.Paragraph>
      {/* Enter on a focused choice confirms it, like every other form here. */}
      <div
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault()
            handleSubmit()
          }
        }}
      >
        <Radio.Group
          className={"w-full"}
          onChange={(event) => setSelectedId(event.target.value)}
          value={selectedId}
        >
          <Space direction={"vertical"}>
            {choices.map((choice) => (
              <Radio key={choice.id} value={choice.id}>
                {choice.name}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </div>
      {needsDescription && (
        <div className={"mt-3"}>
          <label htmlFor={"project-education-other-description"}>
            <Typography.Text>
              {t("projectEducation.projectTypeConfirm.otherLabel")}
            </Typography.Text>
          </label>
          <TextInput
            id={"project-education-other-description"}
            value={description}
            maxLength={MAX_DESCRIPTION_LENGTH}
            placeholder={t(
              "projectEducation.projectTypeConfirm.otherPlaceholder"
            )}
            onChange={(event) => setDescription(event.target.value)}
            onPressEnter={handleSubmit}
            status={
              trimmedDescription.length > 0 && !descriptionValid
                ? "error"
                : undefined
            }
          />
          {trimmedDescription.length > 0 && !descriptionValid && (
            <Typography.Text type={"danger"} className={"text-xs"}>
              {t("projectEducation.projectTypeConfirm.otherError")}
            </Typography.Text>
          )}
        </div>
      )}
    </Modal>
  )
}
